import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { LoaderProps } from './types'
import {
  QUERY_STORE_ORDER_TYPE_CAPACITY,
  OrderTypeCapacity
} from 'components/Locations/LocationQueries'
import { QUERY_STORE_ID_BY_SLUG } from 'router/queries'
import { useLazyQuery, useQuery } from '@apollo/client'
import { useStoreOrderTypeCapacity } from 'hooks/useGlobalContext/useStoreOrderTypeCapacity'

export const StoreOrderTypeCapacityLoader = ({
  isParentLoaded = true,
  children
}: LoaderProps) => {
  const { slug } = useParams<{ slug: string }>()

  const { data: storeData, loading: isLoadingStore } = useQuery<{
    stores: { id: string }[]
  }>(QUERY_STORE_ID_BY_SLUG, {
    variables: { slug },
    skip: !slug
  })
  const storeId = storeData?.stores?.[0]?.id

  const { hasCapacity, needsFetch, setCapacity } =
    useStoreOrderTypeCapacity(storeId)

  const [getStoreOrderTypeCapacity, { data, error }] = useLazyQuery<{
    store_order_type_settings: OrderTypeCapacity[]
  }>(QUERY_STORE_ORDER_TYPE_CAPACITY, {
    variables: { store_id: storeId },
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (!needsFetch) {
      return
    }

    getStoreOrderTypeCapacity()
  }, [storeId, needsFetch])

  useEffect(() => {
    if (data?.store_order_type_settings) {
      setCapacity(data.store_order_type_settings)
    }
  }, [data])

  // Gate on the cache, not on the lazy query's `loading`, which is still false
  // between the store resolving and the query firing - long enough to mount
  // children against empty capacity and immediately tear them down again.
  // Capacity never arrives for a slug with no store, or a failed query, so
  // those open the gate too rather than spinning forever.
  const hasNothingToLoad = !isLoadingStore && !storeId
  const isLoaded =
    isParentLoaded && (hasCapacity || hasNothingToLoad || !!error)

  return <React.Fragment>{children(isLoaded)}</React.Fragment>
}
